import { isBefore } from 'date-fns'
import { FormikProvider, useFormik } from 'formik'
import { FormikContextType } from 'formik/dist/types'
import set from 'lodash/set'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnonymousLearningItem } from '../../models/AnonymousLearningItem'
import { LearningItemStatus } from '../../models/BaseModels'
import { Category } from '../../models/Category'
import { Provider } from '../../models/Provider'
import {
  userCanCreateCategory,
  userCanCreateGlobalScheduledCourse,
  userCanCreateOnDemandCourse,
  userCanCreateProvider,
  userCanCreateScheduledCourse,
} from '../../state/calculations/permissions/userCan'
import useCreateOnDemandCourse from '../../state/mutations/useCreateOnDemandCourse'
import useCreateScheduledCourse from '../../state/mutations/useCreateScheduledCourse'
import useUpdateOnDemandCourse from '../../state/mutations/useUpdateOnDemandCourse'
import useUpdateScheduledCourse from '../../state/mutations/useUpdateScheduledCourse'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import setFormErrors from '../../utilities/setFormErrors'
import useFocusInputWithError from '../../utilities/useFocusInputWithError'
import Button from '../basics/Button/Button'
import { CheckboxFormik } from '../basics/Checkbox/Checkbox'
import FormButtons from '../basics/FormButtons/FormButtons'
import useImageInput from '../basics/ImageInput/useImageInput'
import { MarkdownEditorFormik } from '../basics/MarkdownEditor/MarkdownEditor'
import { SelectFormik } from '../basics/Select/Select'
import { TextInputFormik } from '../basics/TextInput/TextInput'
import { SelectCategoriesFormik } from '../formElements/SelectCategories/SelectCategories'
import { SelectLanguagesFormik } from '../formElements/SelectLanguages/SelectLanguages'
import { SelectProviderFormik } from '../formElements/SelectProvider/SelectProvider'
import styles from './EditLearningItemForm.module.scss'
import ScheduledCourseEventsForm, { ScheduledCourseEventsFormValues } from './ScheduledCourseEventsForm'
import PermissionWrapper, { hasAtLeastOnePermission } from '../PermissionWrapper/PermissionWrapper'

type CommonFormValues = {
  uuid: string | null | undefined
  clientUuid: string | null | undefined
  isGlobal: boolean
  status: LearningItemStatus
  title: string
  description: string
  categories: Category[]
  image: string | null | undefined
  provider: Provider | null | undefined
  certificateMode: 'none' | 'limited' | 'unlimited'
  certificateValidity: Date | null | undefined
  isAnyoneAssigned: boolean
}

type ScheduledCourseFormValues = { kind: 'scheduledCourse' } & CommonFormValues & ScheduledCourseEventsFormValues
type OnDemandCourseFormValues = { kind: 'onDemandCourse' } & CommonFormValues & {
    durationInMin: number
    contentLocales: { uuid: string }[]
    externalId: string
  }

type FormValues = ScheduledCourseFormValues | OnDemandCourseFormValues

function useInitialValues(
  learningItemUuid: string | undefined,
  learningItem: AnonymousLearningItem,
  kind?: 'scheduledCourse' | 'onDemandCourse'
): FormValues {
  const { user: currentUser } = useSession()

  const existing = learningItemUuid && !learningItem.isDummy

  const effectiveKind = existing ? learningItem.kind : kind || 'onDemandCourse'

  return useMemo(() => {
    const common = {
      uuid: existing ? learningItem.uuid : null,
      clientUuid: (existing ? learningItem.clientUuid : currentUser?.clientUuid) || '',
      isGlobal: (existing && !learningItem.clientUuid) || false,
      status: existing ? learningItem.status : LearningItemStatus.Draft,
      title: existing ? learningItem.title : '',
      description: existing ? learningItem.description || '' : '',
      categories: existing ? learningItem.categories : [],
      image: existing ? learningItem.imageFileName : undefined,
      provider: existing ? learningItem.provider : null,
      certificateMode:
        existing && learningItem.createCertificate
          ? learningItem.certificateValidUntil
            ? 'limited'
            : 'unlimited'
          : 'none',
      certificateValidity: existing ? learningItem.certificateValidUntil : null,
      isAnyoneAssigned: existing
        ? learningItem.userLearningAssignments
          ? learningItem.userLearningAssignments.length > 0
          : false
        : false,
    } as const

    if (effectiveKind === 'scheduledCourse') {
      return {
        kind: effectiveKind,
        ...common,
        events:
          existing && learningItem.kind === 'scheduledCourse'
            ? learningItem.scheduledCourse.events.map(({ uuid, maxAttendees, dates, participants }) => ({
                uuid: uuid as string | undefined,
                bookings: participants.length,
                maxAttendees: maxAttendees || 0,
                dates: dates.map(({ uuid, title, startDate, endDate, meetingLink, meetingAddress, trainers }) => ({
                  uuid: uuid as string | undefined,
                  title: title || '',
                  startDate: startDate as Date | null,
                  endDate: endDate as Date | null,
                  meetingLink,
                  meetingAddress,
                  trainers: trainers?.map(({ uuid }) => uuid) || [],
                })),
              }))
            : [
                {
                  uuid: null,
                  bookings: 0,
                  maxAttendees: 1,
                  dates: [
                    {
                      uuid: undefined,
                      title: '',
                      startDate: null,
                      endDate: null,
                      meetingLink: null,
                      meetingAddress: null,
                      trainers: [],
                    },
                  ],
                },
              ],
      }
    }

    if (effectiveKind === 'onDemandCourse') {
      return {
        kind: effectiveKind,
        ...common,
        durationInMin:
          existing && learningItem.kind === 'onDemandCourse' ? learningItem.onDemandCourse.durationInMin : 0,
        contentLocales: (existing && learningItem.kind === 'onDemandCourse'
          ? learningItem.onDemandCourse.contentLocales || []
          : []
        ).map((l) => ({ uuid: l })),
        externalId: existing && learningItem.kind === 'onDemandCourse' ? learningItem.onDemandCourse.externalId : '',
      }
    }

    throw new Error(`Can not edit learning item of kind ${effectiveKind}`)
  }, [learningItemUuid, learningItem])
}

type EditLearningItemFormikContextType = FormikContextType<FormValues>

/**********************************************************************
 * LearningItemImageForm
 **********************************************************************/
const LearningItemImageForm = React.memo(function LearningItemImageForm({
  formik,
}: {
  formik: EditLearningItemFormikContextType
}) {
  const { t } = useTranslation()
  const setValue = (value: string | null | undefined) => formik.setFieldValue('image', value, true)

  const imageInput = useImageInput({ value: formik.values.image, setValue, initialValue: formik.initialValues.image })

  return (
    <>
      <div className={styles.LearningItemImageForm}>
        <div {...imageInput.rootProps} className={styles.image}>
          <img src={formik.values.image || undefined} />
          <input {...imageInput.inputProps} />
        </div>
        <div className={styles.actions}>
          {!imageInput.discardButtonProps.disabled && (
            <Button small {...imageInput.discardButtonProps}>
              {t('generic cta.discard')}
            </Button>
          )}
          {!imageInput.removeButtonProps.disabled && (
            <Button small {...imageInput.removeButtonProps}>
              {t('generic cta.remove')}
            </Button>
          )}
          {!imageInput.adjustButtonProps.disabled && (
            <Button small filled {...imageInput.adjustButtonProps}>
              {t('cta crop image')}
            </Button>
          )}
          <Button small filled {...imageInput.selectButtonProps}>
            {t('generic cta.upload')}
          </Button>
        </div>
      </div>
      {imageInput.children}
    </>
  )
})

/**********************************************************************
 * EditLearningItemForm
 **********************************************************************/
export default React.memo(function EditLearningItemForm({
  kind,
  learningItemUuid,
  onClose,
  learningItem,
}: {
  kind?: 'scheduledCourse' | 'onDemandCourse'
  learningItemUuid?: string
  onClose: () => void
  learningItem: AnonymousLearningItem
}) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()

  if (learningItem.error) {
    onClose()
  }

  const createScheduledCourse = useCreateScheduledCourse()
  const updateScheduledCourse = useUpdateScheduledCourse()
  const createOnDemandCourse = useCreateOnDemandCourse()
  const updateOnDemandCourse = useUpdateOnDemandCourse()
  const formik: EditLearningItemFormikContextType = useFormik({
    initialValues: useInitialValues(learningItemUuid, learningItem, kind),
    enableReinitialize: true,
    validate: (values) => {
      const errors = {}

      if (hasAtLeastOnePermission(learningItem.permissions, 'edit') && values.kind === 'scheduledCourse') {
        for (const [eventIdx, event] of values.events.entries()) {
          for (const [dateIdx, date] of event.dates.entries()) {
            if (!date.meetingLink && !date.meetingAddress) {
              set(errors, `events.${eventIdx}.dates.${dateIdx}.meetingLink`, t('errors.link or address required'))
              set(errors, `events.${eventIdx}.dates.${dateIdx}.meetingAddress`, t('errors.link or address required'))
            }
            if (date.startDate && date.endDate && isBefore(date.endDate, date.startDate)) {
              set(errors, `events.${eventIdx}.dates.${dateIdx}.endDate`, t('errors.end date must be after start date'))
            }
          }
          if (!event.dates.length) {
            set(errors, `events.${eventIdx}.datesArray`, t('errors.event must have dates'))
          }
        }
        if (!values.events.length) {
          set(errors, `eventsArray`, t('errors.course must have events'))
        }
      }

      if (values.status === LearningItemStatus.Draft) {
        if (
          (learningItem.kind === 'onDemandCourse' && learningItem.onDemandCourse.hasBookings) ||
          (learningItem.kind === 'scheduledCourse' && learningItem.scheduledCourse.hasBookings) ||
          values.isAnyoneAssigned
        ) {
          set(errors, 'status', t('learning item.status draft error'))
        }
      }

      return errors
    },
    onSubmit: async (values, { setStatus, setErrors }) => {
      try {
        setStatus()
        const common = {
          status: values.status,
          uuid: values.uuid!,
          title: values.title,
          isFavorite: false,
          description: values.description!,
          image: values.image !== learningItem.imageFileName ? values.image?.replace(/^[^,]+,/, '') || null : undefined,
          categoryUuids: values.categories.map(({ uuid }) => uuid),
          providerUuid: values.provider?.uuid,
          isGlobalProvider: values.provider?.clientUuid === null,
          clientUuid: values.isGlobal ? null : values.clientUuid,
          createCertificate: values.certificateMode !== 'none',
          certificateValidUntil: values.certificateMode === 'limited' ? values.certificateValidity : null,
        }

        let result: { uuid: string } | undefined = undefined
        if (values.kind === 'scheduledCourse') {
          const data = {
            ...common,
            events: values.events.map((event) => ({
              uuid: event.uuid!,
              maxAttendees: event.maxAttendees!,
              dates: event.dates.map((date) => ({
                uuid: date.uuid!,
                title: date.title!,
                trainers: date.trainers,
                startDate: date.startDate!,
                endDate: date.endDate!,
                meetingLink: date.meetingLink,
                meetingAddress: date.meetingAddress,
              })),
            })),
          }

          result = learningItemUuid
            ? await updateScheduledCourse.mutateAsync(data)
            : await createScheduledCourse.mutateAsync(data)
        } else {
          const data = {
            ...common,
            durationInMin: values.durationInMin,
            contentLocales: values.contentLocales.map(({ uuid }) => uuid),
            externalId: values.externalId,
          }
          result = learningItemUuid
            ? await updateOnDemandCourse.mutateAsync(data)
            : await createOnDemandCourse.mutateAsync(data)
        }

        routes.course.goto({ uuid: result!.uuid })
      } catch (err) {
        setFormErrors({ setStatus, setErrors, t }, err)
      }
    },
  })

  useFocusInputWithError(formik)

  if (learningItem.isDummy && (userCanCreateScheduledCourse(currentUser) || userCanCreateOnDemandCourse(currentUser))) {
    // This component is also used for creating new learning items. In
    // this case, we need to set the permissions to edit, because
    // by default, a dummy learning item has them set to false.
    setPermissionsToLearningItem(learningItem)
  }

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit} className={styles.EditLearningItemForm}>
        <div className={styles.titleAndStatus}>
          <PermissionWrapper toCheck={learningItem.permissions} hasAtLeastOne="edit">
            <TextInputFormik name="title" label={t('learning item.field title')} required />

            {userCanCreateGlobalScheduledCourse(currentUser) && (
              <CheckboxFormik
                name="isGlobal"
                label={t('learning item.field is global')}
                className={styles.isGlobal}
                disabled={Boolean(learningItemUuid)}
              />
            )}
          </PermissionWrapper>
        </div>

        <div className={styles.imageAndDetailsForm}>
          <PermissionWrapper toCheck={learningItem.permissions} hasAtLeastOne={['edit_image', 'edit']}>
            <LearningItemImageForm formik={formik} />
          </PermissionWrapper>

          <div className={styles.detailsForm}>
            <PermissionWrapper toCheck={learningItem.permissions} hasAtLeastOne="edit">
              <div>
                <SelectFormik
                  name="status"
                  label={t('learning item.edit field status')}
                  options={[
                    { value: 'draft', label: t('learning item.status.draft') },
                    { value: 'published', label: t('learning item.status.published') },
                    { value: 'deprecated', label: t('learning item.status.deprecated') },
                  ]}
                />
                <SelectProviderFormik
                  name="provider"
                  label={t('learning item.field provider')}
                  allowCreate={userCanCreateProvider(currentUser)}
                />
                <SelectCategoriesFormik
                  name="categories"
                  label={t('learning item.field categories')}
                  allowCreate={userCanCreateCategory(currentUser)}
                />

                {formik.values.kind === 'onDemandCourse' && (
                  <>
                    <TextInputFormik
                      name="externalId"
                      label={t('learning item.field external id')}
                      disabled={Boolean(learningItemUuid)}
                      required
                    />
                    <TextInputFormik
                      type="number"
                      name="durationInMin"
                      label={t('learning item.field duration in min')}
                      min={1}
                      required
                    />
                    <SelectLanguagesFormik label={t('learning item.contentLanguage')} name="contentLocales" required />
                  </>
                )}
                <SelectFormik
                  name="certificateMode"
                  label={t('learning item.field certificate mode')}
                  options={[
                    { value: 'none', label: t('learning item.certificate mode.none') },
                    { value: 'limited', label: t('learning item.certificate mode.limited') },
                    { value: 'unlimited', label: t('learning item.certificate mode.unlimited') },
                  ]}
                />
                {formik.values.certificateMode === 'limited' && (
                  <TextInputFormik
                    type="date"
                    name="certificateValidity"
                    label={t('learning item.field certificate validity')}
                    required
                  />
                )}
              </div>
            </PermissionWrapper>
            <div>
              <PermissionWrapper toCheck={learningItem.permissions} hasAtLeastOne={['edit_description', 'edit']}>
                <MarkdownEditorFormik name="description" label={t('learning item.field description')} required />
              </PermissionWrapper>
            </div>
          </div>
        </div>

        <PermissionWrapper toCheck={learningItem.permissions} hasAtLeastOne="edit">
          {formik.values.kind === 'scheduledCourse' && <ScheduledCourseEventsForm formik={formik as any} />}
        </PermissionWrapper>

        <FormButtons {...formik} onClose={onClose} />
      </form>
    </FormikProvider>
  )
})

function setPermissionsToLearningItem(learningItem: AnonymousLearningItem) {
  learningItem.permissions.edit = true
}
